import type { Chat } from './chat.types';
import type { ChangeEvent, FormEvent, KeyboardEvent, RefObject } from 'react';

import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import {
  CloseIcon,
  IconButton,
  SendIcon,
  SmileyIcon,
  Spinner,
  TextArea,
  Tooltip,
  classNames,
  useOnClickOutside,
} from '@movingimage-evp/mi-ui-component-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import chatStyles from './chat.module.css';
import styles from './message-form.module.css';

type Props = {
  value: string;
  cancelButtonVisible?: boolean;
  error?: Chat['userActionError'];
  loading?: boolean;
  testId?: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
};

export function MessageForm({
  value,
  cancelButtonVisible,
  error,
  loading,
  testId = 'chat-form',
  onChange,
  onSubmit,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const emojiWrapperRef = useRef<HTMLDivElement>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const submitOnEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  };

  const handleEmojiButtonClick = () => {
    setEmojiPickerOpen((state) => !state);
  };

  const onEmojiClick = ({ native }: { native: string }) => {
    onChange(value ? `${value} ${native}` : native);
    setEmojiPickerOpen(false);
  };

  useOnClickOutside(emojiWrapperRef as RefObject<HTMLElement>, () => setEmojiPickerOpen(false));

  return (
    <form data-testid={testId} className={styles.messageForm} onSubmit={handleSubmit}>
      {error && <span className={classNames(chatStyles.popup, styles.errorPopup)}>{error}</span>}

      <div ref={emojiWrapperRef} className={styles.emojiPickerWrapper}>
        <Tooltip label={t('common.chat.newMessageInput.emoticonButton')} position="right">
          <IconButton
            rounded
            type="button"
            data-testid={`${testId}-emoji-button`}
            onClick={handleEmojiButtonClick}
            aria-label={t('common.chat.newMessageInput.emoticonButton')}
          >
            <SmileyIcon />
          </IconButton>
        </Tooltip>
        {emojiPickerOpen && <Picker data={data} onEmojiSelect={onEmojiClick} />}
      </div>

      <TextArea
        data-testid={`${testId}-text-input`}
        placeholder={t('common.chat.newMessageInput.placeholder')}
        className={styles.input}
        autoSize
        rows={1}
        value={value}
        onKeyDown={submitOnEnter}
        onChange={handleMessageChange}
      >
        <Tooltip label={t('common.chat.newMessageInput.sendMessage')}>
          <IconButton
            rounded
            data-testid={`${testId}-submit-message-button`}
            aria-label={t('common.chat.newMessageInput.sendMessage')}
          >
            {loading ? <Spinner /> : <SendIcon />}
          </IconButton>
        </Tooltip>
      </TextArea>

      {cancelButtonVisible && (
        <Tooltip label={t('common.chat.newMessageInput.cancelEditingButton')}>
          <IconButton
            rounded
            type="button"
            data-testid={`${testId}-cancel-edit-button`}
            onClick={onCancel}
            aria-label={t('common.chat.newMessageInput.cancelEditingButton')}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      )}
    </form>
  );
}
